import MessagingCard from '@orn/shared-web/components/MessagingCard';
import { AlertCircleIcon } from '@orn/shared-web/icons';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import { PartnerRelationship } from '../../../__generated__/globalTypes';
import { useCreateDependentAndRelationship } from '../../../hooks/mutations/useCreateDependentAndRelationship';
import { useDeleteDependent } from '../../../hooks/mutations/useDeleteDependent';
import { useLinkDependents } from '../../../hooks/mutations/useLinkDependents';
import { useUpdateCaregiverRelationship } from '../../../hooks/mutations/useUpdateCaregiverRelationship';
import { useUpdateDependent } from '../../../hooks/mutations/useUpdateDependent';
import { useDependents } from '../../../hooks/queries/useDependents';
import { formatRelationship, toIsoDate } from '../../../lib/dependents/helpers';
import { OnboardingTitle } from '../../OnboardingLayout';
import { buildReviewTitle } from '../dependents/helpers';
import { useOnboardingContext, type IPendingDependent } from '../OnboardingContext';

import OnboardingStepActions from './OnboardingStepActions';

import type { Scalars, CaregiverRelationshipKind } from '../../../__generated__/globalTypes';
import type { IDependent } from '../../../hooks/queries/useDependents';
import type { IOnboardingStepProps } from '../types';
import type { ISummaryCell } from '@orn/shared-web/components/MessagingCard';

const formatDob = (iso: string | null): string => {
  if (!iso) {
    return '—';
  }
  // The CalendarDate scalar serializes to YYYY-MM-DD; reformat to MM/DD/YYYY
  // for display without pulling in a date library.
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return iso;
  }
  const [, year, mm, dd] = match;
  return `${mm}/${dd}/${year}`;
};

// Build the summary cells from a slot's values, then chunk into two-up rows.
// The optional Preferred name cell shifts the pairing, so we chunk the flat
// list rather than hardcoding pairs.
const buildSummaryRows = (values: IPendingDependent['values']): ISummaryCell[][] => {
  const cells: ISummaryCell[] = [
    { label: 'First name', value: values.firstName || '—' },
    { label: 'Last name', value: values.lastName || '—' },
  ];
  if (values.preferredName.trim() !== '') {
    cells.push({ label: 'Preferred name', value: values.preferredName });
  }
  cells.push({
    label: 'Relationship',
    value:
      formatRelationship({
        relationship: values.relationship,
        isStep: values.isStep,
        isInLaw: values.isInLaw,
      }) || '—',
  });
  cells.push({ label: 'Date of birth', value: formatDob(toIsoDate(values.dateOfBirth)) });

  const rows: ISummaryCell[][] = [];
  for (let i = 0; i < cells.length; i += 2) {
    rows.push(cells.slice(i, i + 2));
  }
  return rows;
};

/**
 * Final step of the loved-ones flow and the **single persistence point** for
 * the dependent entry chain. Reads pending slots from `OnboardingContext`
 * — the entry step writes there only — and on submit:
 *
 * 1. Deletes any dependent ids in `pendingDependentDeletions` — slots the user dropped
 *    by shrinking the count. The backend cascade-deletes the caregiver edge,
 *    any partnership, residences, and vehicles tied to the row.
 * 2. Creates a row + caregiver edge for each slot without `serverId`.
 * 3. Updates the dependent + caregiver-relationship rows for each slot that
 *    has a `serverId` (i.e. seeded from server or already created by a prior
 *    pass through this screen).
 * 4. Links the two dependents as spouses when count = 2 and no partnership
 *    exists yet — idempotent thanks to the DB's `dependant_partnerships_pair_unique`.
 *
 * Refetch policy: create returns the parent `Family` and update writes back to
 * normalized entities, so both refresh the cache on their own. Only a create
 * or delete changes the dependent *set* (delete returns a bare id the cache
 * can't reconcile; create needs us to learn the new server ids) — so the step
 * refetches exactly once, and only then, to get the authoritative ordered list
 * for the link decision and to back-fill new `serverId`s into the pending slots
 * (so an edit → re-submit round-trip takes the update path, not a second create).
 *
 * Edit on a card sets `OnboardingContext.editingDependentIndex` and steps back
 * to `DependentEntryStep`, which opens on that slot and continues forward
 * through the flow (Edit person 1 → person 1 → person 2 → review). The plain
 * back-arrow leaves the index null and just steps back to the entry screen,
 * which opens on slot 0 — the user retraverses the flow from the first person.
 *
 * Figma: Mantle - UX/UI, node 4161:104788 ("Onboarding Step 09 - Loved Ones Review").
 */
const DependentReviewStep = ({ onNext, onBack }: IOnboardingStepProps) => {
  const { dependents, partnershipExists, loading, refetch } = useDependents();
  const [createDependent] = useCreateDependentAndRelationship();
  const [deleteDependent] = useDeleteDependent();
  const [updateDependent] = useUpdateDependent();
  const [updateRelationship] = useUpdateCaregiverRelationship();
  const [linkDependents] = useLinkDependents();
  const {
    dependentCount,
    pendingDependents,
    setPendingDependents,
    pendingDependentDeletions,
    setPendingDependentDeletions,
    setEditingDependentIndex,
  } = useOnboardingContext();
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const effectiveCount = dependentCount ?? pendingDependents.length;
  const slots = pendingDependents.slice(0, effectiveCount);

  // Edit-on-card is the only thing that sets `editingDependentIndex` — it tells
  // the entry step which slot to open on; from there the user continues forward
  // through the flow. The plain back arrow leaves it null so the entry step
  // opens on slot 0 and the user retraverses the flow.
  const handleEdit = React.useCallback(
    (index: number) => {
      setEditingDependentIndex(index);
      onBack?.();
    },
    [setEditingDependentIndex, onBack]
  );

  const handleSubmit = React.useCallback(async () => {
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      // Phase 0 — delete any dependents the user dropped via the count step.
      // The backend cascade handles partnership, caregiver-edge, residence,
      // and vehicle rows. Done first so a later link in this same submit
      // doesn't trip on a partnership row pointing at a doomed dependent.
      if (pendingDependentDeletions.length > 0) {
        await Promise.all(
          pendingDependentDeletions.map((dependentId) =>
            deleteDependent({ variables: { input: { dependantId: dependentId } } })
          )
        );
        setPendingDependentDeletions([]);
      }

      // Phase 1 — create any new slots. The mutation returns the parent
      // `Family`, so Apollo's normalized cache refreshes the dependent/state
      // queries on its own; we don't read per-create ids here. The Phase 3
      // refetch gives us the authoritative ordered list for linking + id
      // back-fill.
      let createdAny = false;
      for (const slot of slots) {
        if (slot.serverId) {
          continue;
        }
        const isoDob = toIsoDate(slot.values.dateOfBirth);
        await createDependent({
          variables: {
            input: {
              firstName: slot.values.firstName,
              lastName: slot.values.lastName,
              preferredName: slot.values.preferredName === '' ? null : slot.values.preferredName,
              dateOfBirth: (isoDob as unknown as Scalars['CalendarDate']['input']) ?? null,
              caregiverRelationship: {
                relationship: slot.values.relationship as CaregiverRelationshipKind,
                isStep: slot.values.isStep,
                isInLaw: slot.values.isInLaw,
                relationshipOther: null,
                livesWithCaregiver: null,
              },
            },
          },
        });
        createdAny = true;
      }

      // Phase 2 — update any pre-existing slots (seeded from server, or
      // already created and now being re-submitted after an edit). The update
      // mutations write back to the normalized `Dependant` / `CaregiverRelationship`
      // entities, so the cache refreshes without a refetch.
      const updates: Promise<unknown>[] = [];
      for (const slot of slots) {
        if (!slot.serverId || !slot.caregiverRelationshipId) {
          continue;
        }
        const isoDob = toIsoDate(slot.values.dateOfBirth);
        updates.push(
          updateDependent({
            variables: {
              input: {
                dependantId: slot.serverId,
                firstName: slot.values.firstName,
                lastName: slot.values.lastName,
                preferredName: slot.values.preferredName === '' ? null : slot.values.preferredName,
                dateOfBirth: (isoDob as unknown as Scalars['CalendarDate']['input']) ?? null,
              },
            },
          })
        );
        updates.push(
          updateRelationship({
            variables: {
              input: {
                caregiverRelationshipId: slot.caregiverRelationshipId,
                relationship: slot.values.relationship,
                isStep: slot.values.isStep,
                isInLaw: slot.values.isInLaw,
              },
            },
          })
        );
      }
      if (updates.length > 0) {
        await Promise.all(updates);
      }

      // Phase 3 — only a create or delete changes the dependent *set* (delete
      // returns a scalar id the cache can't reconcile; create needs us to learn
      // the new server ids). When either happened, refetch once for the
      // authoritative ordered list, back-fill the new server ids into pending
      // (existing slots match by id, the rest map to the new slots in order),
      // and re-derive partnership state. A pure-update submit reads the
      // already-fresh reactive values instead.
      let finalDependents: ReadonlyArray<IDependent> = dependents;
      let alreadyPartnered = partnershipExists;
      if (createdAny || pendingDependentDeletions.length > 0) {
        const snapshot = await refetch();
        finalDependents = snapshot.dependents;
        const knownIds = new Set(slots.map((s) => s.serverId).filter((id): id is string => id !== null));
        const createdDeps = finalDependents.filter((d) => !knownIds.has(d.id));
        let nextCreated = 0;
        setPendingDependents((prev) =>
          prev.map((slot, i) => {
            if (slot.serverId || i >= effectiveCount) {
              return slot;
            }
            const dep = createdDeps[nextCreated];
            nextCreated += 1;
            return dep ? { ...slot, serverId: dep.id, caregiverRelationshipId: dep.caregiverRelationshipId } : slot;
          })
        );
        if (pendingDependentDeletions.length > 0) {
          setPendingDependentDeletions([]);
        }
        alreadyPartnered =
          finalDependents.length === 2 ? snapshot.arePartnered(finalDependents[0].id, finalDependents[1].id) : false;
      }

      // Phase 4 — link the pair when there are two dependents and they aren't
      // already partnered.
      if (effectiveCount === 2 && finalDependents.length === 2 && !alreadyPartnered) {
        await linkDependents({
          variables: {
            input: {
              dependantAId: finalDependents[0].id,
              dependantBId: finalDependents[1].id,
              relationship: PartnerRelationship.Spouse,
              relationshipOther: null,
              liveTogether: null,
            },
          },
        });
      }

      onNext();
    } catch (err) {
      logger.error('DependentReviewStep submit failed', err);
      setSubmitError(
        'Something went wrong saving your loved ones. Please try again — contact help@gomantle.com if the problem persists.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    createDependent,
    deleteDependent,
    dependents,
    effectiveCount,
    linkDependents,
    pendingDependentDeletions,
    setPendingDependentDeletions,
    onNext,
    partnershipExists,
    refetch,
    setPendingDependents,
    slots,
    updateDependent,
    updateRelationship,
  ]);

  if (loading && slots.length === 0) {
    return null;
  }

  return (
    <>
      <OnboardingTitle title={buildReviewTitle(slots.map((s) => s.values))} onBack={onBack} />

      <div className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-3">
          {slots.map((slot, i) => (
            <MessagingCard key={slot.serverId ?? `slot-${i}`} variant="stone">
              <MessagingCard.Summary rows={buildSummaryRows(slot.values)} onEdit={() => handleEdit(i)} />
            </MessagingCard>
          ))}
        </div>

        {submitError && (
          <MessagingCard variant="error">
            <MessagingCard.Text icon={<AlertCircleIcon />}>{submitError}</MessagingCard.Text>
          </MessagingCard>
        )}

        <OnboardingStepActions
          continueLabel="Next"
          onContinue={() => void handleSubmit()}
          isContinueLoading={isSubmitting}
          isContinueDisabled={slots.length === 0}
        />
      </div>
    </>
  );
};

DependentReviewStep.displayName = 'DependentReviewStep';

export default DependentReviewStep;
